import { Injectable, OnDestroy } from '@angular/core';
import { EMPTY, Observable, ReplaySubject } from 'rxjs';
import { catchError, take, timeout } from 'rxjs/operators';

import {
  Cb2PreviewRuntimePayload,
  hasPreviewMarketplaceStub,
  isCb2PreviewInboundMessage,
} from './cb2-preview-protocol';

/** Max wait for the dashboard stub before the boot gives up (ms). */
export const CB2_PREVIEW_BOOT_TIMEOUT_MS = 10_000;

/**
 * Coordinates the CB2 preview boot sequence.
 *
 * The dashboard pushes a full marketplace stub to the iframe via postMessage
 * (on iframe load + each soft-refresh). In preview mode the webapp must NOT
 * call `getMarketplaceByDomain`: `MarketplaceService.loadMarketplace$` awaits
 * the first payload carrying a marketplace stub, then applies it.
 *
 * This service registers its `message` listener early (from an APP_INITIALIZER
 * that runs before `ensureMarketplaceIsLoaded`) so the stub is captured even if
 * the dashboard dispatches it before the bridge is armed. The listener only
 * feeds the initial-payload subject; ongoing refresh handling stays with
 * `PreviewBridgeService`.
 */
@Injectable({ providedIn: 'root' })
export class Cb2PreviewBootService implements OnDestroy {
  private readonly initialPayload$ = new ReplaySubject<Cb2PreviewRuntimePayload>(1);
  private listener: ((event: MessageEvent) => void) | null = null;

  /** Registers the early postMessage listener. Idempotent. */
  registerListener(): void {
    if (this.listener) return;
    this.listener = (event: MessageEvent) => this.onMessage(event);
    window.addEventListener('message', this.listener);
  }

  /**
   * Resolves with the first inbound payload carrying a marketplace stub.
   * Completes after a single emission (the boot is a one-shot).
   *
   * Gives up after {@link CB2_PREVIEW_BOOT_TIMEOUT_MS} so `loadMarketplace$`
   * never hangs forever — on timeout it completes without emitting and the app
   * stays on its initial loading screen (graceful fallback).
   */
  waitForInitialPayload(
    timeoutMs = CB2_PREVIEW_BOOT_TIMEOUT_MS,
  ): Observable<Cb2PreviewRuntimePayload> {
    return this.initialPayload$.asObservable().pipe(
      take(1),
      timeout(timeoutMs),
      catchError(() => EMPTY),
    );
  }

  ngOnDestroy(): void {
    if (this.listener) {
      window.removeEventListener('message', this.listener);
      this.listener = null;
    }
  }

  private onMessage(event: MessageEvent): void {
    const data = event.data;
    if (!isCb2PreviewInboundMessage(data)) return;
    // Only payload-bearing commands can bootstrap the marketplace.
    if (data.type !== 'refresh-preview' && data.type !== 'set-preview-locale') return;
    if (!hasPreviewMarketplaceStub(data.payload)) return;
    this.initialPayload$.next(data.payload);
  }
}
